import { unByKey } from 'cadenza/utils/event-util';

const SELECTED_CLASS = 'selected';
const START_RANGE_CLASS = 'startRange';
const END_RANGE_CLASS = 'endRange';
const IN_RANGE_CLASS = 'inRange';

export class FlatpickrAbstractPlugin {

  constructor ({ pluginName, elementsPerPage, shifts } = {}) {
    this._pluginName = pluginName;
    this._elementsPerPage = elementsPerPage;
    this.shifts = shifts;
  }

  factory () {
    return (fp) => {
      this._fp = fp;
      return {
        onParseConfig: () => {
          this._fp.config.enableTime = false;
        },
        onValueUpdate: () => this.updateElementsStyle(),
        onKeyDown: (_, __, ___, e) => this._onKeyDown(_, __, ___, e),
        onReady: () => {
          this.clearUnnecessaryDOMElements();
          this.addMonthNavEventListeners();
          this.addElements();
          this.updateElementsStyle();
          this._fp.loadedPlugins.push(this._pluginName);
        },
        onDestroy: () => this._onDestroy(),
        onDayCreate: (selectedDate, formattedDate, fp) => this._onDayCreate(selectedDate, formattedDate, fp)
      };
    };
  }

  /**
   * Modifies the behaviour of the month navigators
   *
   * @protected
   */
  addMonthNavEventListeners () {
  }

  /**
   * Adds the elements to the corresponding container
   *
   * @protected
   */
  addElements () {
  }

  /**
   * Resets the Elements. Should be called when some properties of flatpickr is changed
   * and the plugin does not know about it.
   */
  resetElements () {
    this.addElements();
    this.updateElementsStyle();
  }

  /**
   * Keep this idempotent. Update the style of the elements
   *
   * @protected
   */
  updateElementsStyle () {
    this.updateNavigationCurrentMonth(); // Even if selected dates are not set we want to call this
    if (!this._fp.rContainer || !this._fp.selectedDates || !this._fp.selectedDates.length) {
      return;
    }
    this._elements.forEach(element => element.classList.remove(SELECTED_CLASS, START_RANGE_CLASS, END_RANGE_CLASS, IN_RANGE_CLASS));

    if (this._fp.config.mode === 'single') {
      if (this.isOnCurrentPage(this._fp.selectedDates[0])) {
        this._getClickedElement(this._fp.selectedDates[0]).classList.add(SELECTED_CLASS);
      }
    } else if (this._fp.config.mode === 'range') {
      if (this._fp.selectedDates.length === 1) {
        this._updateElementStyle(this._fp.selectedDates[0], [ START_RANGE_CLASS ]);
      } else if (this._fp.selectedDates.length === 2) {
        this._updateElementStyle(this._fp.selectedDates[0], [ START_RANGE_CLASS ]);
        this._updateElementStyle(this._fp.selectedDates[1], [ END_RANGE_CLASS ]);
        this._updateElementsInRange(this._fp.selectedDates[0], this._fp.selectedDates[1]);
      }
    }
  }

  /**
   * We're using the monthNav elements, to change years (in the year plugin) and differently than in the original implementation in the month plugin
   * So the conditions indicating whether the navs should be shown or not are different
   *
   * @protected
   */
  updateNavigationCurrentMonth () {
  }

  /**
   * Checks whether the selectedDate is on the current date page
   *
   * @protected
   * @param selectedDate - the date to check if it's in the current page or not
   */
  isOnCurrentPage (selectedDate) {
  }

  _onKeyDown (_, __, ___, e) {
    const shouldMove = this.shifts[e.keyCode] !== undefined;
    if (!shouldMove && e.keyCode !== 13) {
      return;
    }
    if (!this._fp.rContainer || !this._elementsContainer) {
      return;
    }
    const currentlySelected = this._elementsContainer.querySelector(SELECTED_CLASS);
    let index = Array.prototype.indexOf.call(this._elementsContainer.children, document.activeElement);
    if (index === -1) {
      const target = currentlySelected || this._elementsContainer.firstElementChild;
      target.focus();
      index = target.$i;
    }
    if (shouldMove) {
      this._elementsContainer.children[(this._elementsPerPage + index + this.shifts[e.keyCode]) % this._elementsPerPage].focus();
    } else if (e.keyCode === 13 && this._elementsContainer.contains(document.activeElement)) {
      this.setDate(document.activeElement.dateObj);
    }
  }

  /**
   * Cleanup of previous html plugin element
   *
   * @protected
   */
  clearUnnecessaryDOMElements () {
    if (!this._fp.rContainer || !this._fp.daysContainer || !this._fp.weekdayContainer) {
      return;
    }
    this._fp.rContainer.removeChild(this._fp.daysContainer);
    this._fp.calendarContainer.classList.add('no-day-picker');
    this._fp.rContainer.removeChild(this._fp.weekdayContainer);
    for (let index = 0; index < this._fp.monthElements.length; index++) {
      const element = this._fp.monthElements[index];
      if (!element.parentNode) {
        continue;
      }
      element.parentNode.removeChild(element);
    }
  }

  /**
   * Removes unnecessary container elements, so that new ones can be added
   *
   * @protected
   * @param elementsContainer - the container whose elements we will remove
   */
  cleanElementsContainer (elementsContainer) {
    if (elementsContainer) {
      unByKey(this._elementsEventKeys);
      this._elementsEventKeys = [];
      elementsContainer.remove();
    }
  }

  _updateElementStyle (selectedDate, addClasses, removeClasses = []) {
    if (this.isOnCurrentPage(selectedDate)) {
      const selectedElement = this._getClickedElement(selectedDate);
      selectedElement.classList.add(SELECTED_CLASS, ...addClasses);
      selectedElement.classList.remove(...removeClasses);
    }
  }

  _updateElementsInRange (startDate, endDate) {
    this._elements.forEach(element => {
      const isElementWithinRange = isElementInRange(element.dateObj, startDate, endDate);
      element.classList.toggle(IN_RANGE_CLASS, isElementWithinRange);
    });
  }

  /**
   * Listener for the mouse over event
   *
   * @protected
   * @param event - the mouse over event
   */
  onMouseoverElement (event) {
    if (this._fp.config.mode === 'range' && this._fp.selectedDates.length === 1) {
      const selectedDate = this._fp.selectedDates[0];
      this._elements.forEach(element => element.classList.remove(SELECTED_CLASS, START_RANGE_CLASS, END_RANGE_CLASS, IN_RANGE_CLASS));
      const currentElement = event.currentTarget;
      const currentElementDate = currentElement.dateObj;
      if (selectedDate.getTime() < currentElementDate.getTime()) {
        this._updateElementStyle(this._fp.selectedDates[0], [ START_RANGE_CLASS ], [ END_RANGE_CLASS ]);
        this._updateElementsInRange(selectedDate, currentElementDate);
        currentElement.classList.add(SELECTED_CLASS, END_RANGE_CLASS);
      } else if (selectedDate.getTime() > currentElementDate.getTime()) {
        this._updateElementStyle(this._fp.selectedDates[0], [ END_RANGE_CLASS ], [ START_RANGE_CLASS ]);
        this._updateElementsInRange(currentElementDate, selectedDate);
        currentElement.classList.add(SELECTED_CLASS, START_RANGE_CLASS);
      } else {
        this._updateElementStyle(this._fp.selectedDates[0], [ START_RANGE_CLASS, END_RANGE_CLASS ]);
      }
    }
  }

  /**
   * Listener called when the user clicks on a month or year
   *
   * @protected
   * @param event - the click event
   */
  onClickElement (event) {
    event.stopImmediatePropagation();
    this.setDate(event.currentTarget.dateObj);
  }

  /**
   * Sets the date to the passed parameter
   *
   * @protected
   * @param date - to be set
   */
  setDate (date) {
    const selectedDate = date;
    this._latestSelectedDateObj = selectedDate;
    this._fp.setDate(this._fp.config.mode === 'range' && this._fp.selectedDates.length === 1
      ? [ this._fp.selectedDates[0], selectedDate ]
      : selectedDate, true);

    this.updateElementsStyle();
  }

  // Hack to avoid jumping to the last date in the selectedDates which is done by default when using this._fp.setDate
  _onDayCreate (selectedDate, formattedDate, fp) {
    if (fp.config.mode === 'range' && selectedDate.length === 2) {
      fp.latestSelectedDateObj = this._latestSelectedDateObj;
    }
  }

  _onDestroy () {
    this.cleanElementsContainer(this._elementsContainer);
    unByKey(this._monthNavEventKeys);
  }

}

export function isElementInRange (currentElementDate, startDate, endDate) {
  return (!startDate || startDate <= currentElementDate)
    && (!endDate || endDate >= currentElementDate);
}
